/**
 * pdf.js helpers: load documents, render pages and thumbnails.
 */

import {
  getDocument,
  GlobalWorkerOptions,
  type PDFDocumentProxy,
  type PDFPageProxy,
  type RenderTask,
} from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export const PDF_RENDER_MAX_EDGE = 2400;
export const PDF_THUMB_WIDTH = 140;

export interface PdfPageSize {
  width: number;
  height: number;
  /** PDF points (1/72") at scale 1 */
  widthPts: number;
  heightPts: number;
}

export interface LoadedPdf {
  proxy: PDFDocumentProxy;
  pageCount: number;
  sourceBytes: ArrayBuffer;
}

/** Deep-copy bytes. Prefer this over sharing buffers with pdf.js (it may detach them). */
export function copyArrayBuffer(src: ArrayBuffer): ArrayBuffer {
  const view = new Uint8Array(src);
  const out = new Uint8Array(view.byteLength);
  out.set(view);
  return out.buffer;
}

export async function loadPdfFromBytes(bytes: ArrayBuffer): Promise<LoadedPdf> {
  // Keep an independent copy for download/export. pdf.js may transfer/detach
  // the ArrayBuffer passed into getDocument.
  const sourceBytes = copyArrayBuffer(bytes);
  const forViewer = copyArrayBuffer(bytes);
  const loadingTask = getDocument({ data: new Uint8Array(forViewer) });
  const proxy = await loadingTask.promise;
  return { proxy, pageCount: proxy.numPages, sourceBytes };
}

export async function loadPdfFromFile(file: File): Promise<LoadedPdf> {
  const bytes = await file.arrayBuffer();
  return loadPdfFromBytes(bytes);
}

export async function getPageSize(
  proxy: PDFDocumentProxy,
  pageNumber: number
): Promise<PdfPageSize> {
  const page = await proxy.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1 });
  return {
    width: viewport.width,
    height: viewport.height,
    widthPts: viewport.width,
    heightPts: viewport.height,
  };
}

export function computeFitScale(
  pageWidth: number,
  pageHeight: number,
  containerWidth: number,
  containerHeight: number,
  padding = 32
): number {
  const availW = Math.max(80, containerWidth - padding);
  const availH = Math.max(80, containerHeight - padding);
  return Math.min(availW / pageWidth, availH / pageHeight, 3);
}

/** Track in-flight renders per canvas so we can cancel stale ones. */
const activeRenders = new WeakMap<HTMLCanvasElement, RenderTask>();

/**
 * Render a PDF page into a canvas.
 * Bakes devicePixelRatio into the viewport scale (avoids transform flip bugs in pdf.js 6).
 */
export async function renderPageToCanvas(
  page: PDFPageProxy,
  canvas: HTMLCanvasElement,
  scale: number
): Promise<{ width: number; height: number; renderTask: RenderTask }> {
  const prev = activeRenders.get(canvas);
  if (prev) {
    try {
      prev.cancel();
    } catch {
      /* ignore */
    }
    activeRenders.delete(canvas);
  }

  const outputScale = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
  // Bake DPR into viewport — do NOT also pass a transform matrix (can invert the page)
  const viewport = page.getViewport({ scale: scale * outputScale });
  const cssWidth = Math.floor(viewport.width / outputScale);
  const cssHeight = Math.floor(viewport.height / outputScale);

  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  canvas.style.width = `${cssWidth}px`;
  canvas.style.height = `${cssHeight}px`;

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Could not get canvas 2D context');

  // Reset any leftover transform/state from prior renders
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const renderTask = page.render({
    canvasContext: ctx,
    // pdf.js 6: when using canvasContext, canvas MUST be null or the page can flip
    canvas: null,
    viewport,
  });
  activeRenders.set(canvas, renderTask);

  try {
    await renderTask.promise;
  } catch (err) {
    // Cancelled renders throw — ignore if a newer render replaced this one
    if ((err as { name?: string })?.name === 'RenderingCancelledException') {
      return { width: cssWidth, height: cssHeight, renderTask };
    }
    throw err;
  } finally {
    if (activeRenders.get(canvas) === renderTask) {
      activeRenders.delete(canvas);
    }
  }

  return { width: cssWidth, height: cssHeight, renderTask };
}

export async function renderPageNumberToCanvas(
  proxy: PDFDocumentProxy,
  pageNumber: number,
  canvas: HTMLCanvasElement,
  scale: number
): Promise<{ width: number; height: number }> {
  const page = await proxy.getPage(pageNumber);
  const result = await renderPageToCanvas(page, canvas, scale);
  return { width: result.width, height: result.height };
}

/** Render a thumbnail data URL for the page list. */
export async function renderThumbnailDataUrl(
  proxy: PDFDocumentProxy,
  pageNumber: number,
  targetWidth = PDF_THUMB_WIDTH
): Promise<string> {
  const page = await proxy.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const scale = targetWidth / base.width;
  const canvas = document.createElement('canvas');
  await renderPageToCanvas(page, canvas, scale);
  return canvas.toDataURL('image/jpeg', 0.72);
}

/** Flatten page (+ optional overlay canvas) to a PNG data URL at a given scale. */
export async function renderPageCompositeDataUrl(
  proxy: PDFDocumentProxy,
  pageNumber: number,
  scale: number,
  overlayCanvas?: HTMLCanvasElement | null
): Promise<{ dataUrl: string; width: number; height: number }> {
  const page = await proxy.getPage(pageNumber);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas 2D context');

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  await page.render({
    canvasContext: ctx,
    canvas: null,
    viewport,
  }).promise;

  if (overlayCanvas && overlayCanvas.width > 0) {
    ctx.drawImage(overlayCanvas, 0, 0, canvas.width, canvas.height);
  }

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: canvas.width,
    height: canvas.height,
  };
}

export function revokeThumbMap(thumbs: Record<number, string>): void {
  for (const url of Object.values(thumbs)) {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  }
}
